import React, { useState, useEffect } from 'react';
import { FaTimes, FaTrash, FaCalendarAlt, FaUsers, FaGift } from 'react-icons/fa';
import { LuDot } from 'react-icons/lu';
import { FiClock } from 'react-icons/fi';
import Calling from '../../api/Calling';
import { toast } from 'react-toastify';
import formatDate from '../../components/formatDate';
import generateCaptcha from '../../components/captcha';

const UserReferralHistoryPlan = ({ userId, onClose, userName }) => {
    const [loading, setLoading] = useState(false);
    const [historyData, setHistoryData] = useState([]);

    // Fetch referral plan history
    const fetchReferralPlanHistory = async () => {
        setLoading(true);
        try {
            const response = await Calling.getReferralPlanHistoryById(userId);
            if (response?.status) {
                setHistoryData(response?.data || []);
            } else {
                toast.error(response?.message || 'Failed to load referral plan history');
            }
        } catch (error) {
            console.error('Error fetching referral plan history:', error);
            toast.error('Failed to load referral plan history');
        } finally {
            setLoading(false);
        }
    };

    // Delete referral plan history
    const handleDeleteHistory = async (historyId) => {
        const content = `You are about to delete this referral plan history. To confirm, please solve this math problem.`;
        const check = generateCaptcha(content);

        if (check) {
            try {
                const response = await Calling.deleteReferralPlanHistory({ ids: [historyId] });
                if (response?.status) {
                    toast.success(response?.message || 'History deleted successfully');
                    fetchReferralPlanHistory();
                } else {
                    toast.error(response?.message || 'Failed to delete history');
                }
            } catch (error) {
                console.error('Error deleting history:', error);
                toast.error('Failed to delete history');
            }
        } else {
            toast.error("Captcha answer is incorrect", { autoClose: 2000 });
        }
    };

    useEffect(() => {
        fetchReferralPlanHistory();
        // eslint-disable-next-line
    }, [userId]);

    const getStatusBadge = (status) => {
        switch (status) {
            case 'active':
                return <span className='px-3 py-1 text-xs rounded-full bg-green-100 text-green-600 flex items-center gap-1'><LuDot size={16} /> Active</span>;
            case 'expired':
                return <span className='px-3 py-1 text-xs rounded-full bg-red-100 text-red-600 flex items-center gap-1'><LuDot size={16} /> Expired</span>;
            case 'cancelled':
                return <span className='px-3 py-1 text-xs rounded-full bg-yellow-100 text-yellow-600 flex items-center gap-1'><LuDot size={16} /> Cancelled</span>;
            default:
                return <span className='px-3 py-1 text-xs rounded-full bg-gray-100 text-gray-600 flex items-center gap-1'><LuDot size={16} /> {status}</span>;
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-main-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col transform transition-all duration-300">
                {/* Modal Header */}
                <div className="flex items-center justify-between p-5 border-b border-main-border bg-gradient-to-r from-blue-500 to-indigo-600 rounded-t-2xl">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-white/20 rounded-lg">
                            <FaUsers className="text-white" size={20} />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-white">Referral Plan History</h2>
                            <p className="text-sm text-white/80">{userName || 'User'}</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-white/20 rounded-full transition-colors"
                    >
                        <FaTimes className="text-white" size={18} />
                    </button>
                </div>

                {/* Modal Body */}
                <div className="flex-1 overflow-auto p-5">
                    {loading ? (
                        <div className="flex items-center justify-center py-10">
                            <svg className="animate-spin h-10 w-10 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        </div>
                    ) : historyData.length > 0 ? (
                        <div className="space-y-4">
                            {historyData.map((history) => (
                                <div
                                    key={history._id}
                                    className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 hover:shadow-md transition-all group"
                                >
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2.5 bg-blue-100 rounded-xl">
                                                <FaGift className="text-blue-500" size={18} />
                                            </div>
                                            <div>
                                                <h3 className="font-semibold text-main-font">{history.r_plan_name}</h3>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <span className="text-xs text-main-subfont bg-blue-100 px-2 py-0.5 rounded">
                                                        {history.r_plan_day} days
                                                    </span>
                                                    <span className="text-xs text-main-subfont bg-indigo-100 px-2 py-0.5 rounded flex items-center gap-1">
                                                        <FaUsers size={10} />
                                                        {history.r_plan_invite_count} invites required
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <div className="text-right">
                                                <p className="text-sm font-medium text-main-font flex items-center justify-end gap-1">
                                                    <FaUsers size={12} className="text-blue-500" />
                                                    {history.u_ref_count} / {history.r_plan_invite_count}
                                                </p>
                                                <p className="text-xs text-main-subfont">Referrals</p>
                                            </div>
                                            {getStatusBadge(history.status)}
                                            <button
                                                onClick={() => handleDeleteHistory(history._id)}
                                                className="p-2 text-red-500 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                                                title="Delete"
                                            >
                                                <FaTrash size={14} />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="mt-3 pt-3 border-t border-blue-200 flex flex-wrap gap-4 text-sm text-main-subfont">
                                        <div className="flex items-center gap-1.5">
                                            <FaCalendarAlt size={12} className="text-green-500" />
                                            <span>Start: {formatDate(history.start_date)}</span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <FaCalendarAlt size={12} className="text-red-500" />
                                            <span>End: {formatDate(history.end_date)}</span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <FiClock size={12} className="text-blue-500" />
                                            <span>Created: {formatDate(history.createdAt)}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-10 text-main-subfont">
                            <div className="p-4 bg-blue-100 rounded-full mb-4">
                                <FaUsers className="text-blue-300" size={40} />
                            </div>
                            <p className="text-lg font-medium text-main-font">No referral plan history</p>
                            <p className="text-sm">This user hasn't enrolled in any referral plans yet.</p>
                        </div>
                    )}
                </div>

                {/* Modal Footer */}
                <div className="flex items-center justify-end p-5 border-t border-main-border bg-gray-50 rounded-b-2xl">
                    <button
                        onClick={onClose}
                        className="px-5 py-2.5 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors font-medium"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UserReferralHistoryPlan;